import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { useAuth } from "@/providers";
import { Collapsible } from "@/shared/components/Collapsible";
import { CollectionSelector } from "@/shared/components/CollectionSelector";
import { DatasetSelector } from "@/shared/components/DatasetSelector";
import { PageLayout } from "@/shared/components/PageLayout";
import {
  ThreadVisibility,
  type LaunchAgentRequest,
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";
import { useAgent, useLaunchAgent } from "@/shared/domain/agents/hooks";
import { type OrgRecord } from "@/shared/domain/orgs";
import { Button } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./LaunchAgent.module.css";
import {
  buildAnalysisScope,
  convertWindowValue,
  type WindowInputMode,
} from "./launchAnalysisScope";
import { seedLaunchValues } from "./seedLaunchValues";

/**
 * Renders one input per declared variable on the agent, plus a
 * visibility radio and a collapsed "Advanced" section (analysis window),
 * then POSTs to the launch route. On success the user lands on the new
 * thread's detail page so the thread appears immediately.
 * ``DATASET_ID`` and ``COLLECTION_ID`` variables render via their
 * respective pickers (:class:`DatasetSelector` / :class:`CollectionSelector`);
 * other types (today just ``STRING``) render as a plain text input with
 * the variable's declared type shown as a badge.
 */
export function LaunchAgent() {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  // The dataset-page "Run agent" dialog navigates here with a
  // ``?dataset_id=<id>`` query param so the dataset is pre-filled
  // without a second click. Empty / missing → no pre-fill.
  const datasetIdFromUrl = searchParams.get("dataset_id") ?? "";
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const agentQuery = useAgent(agentId, { orgId });
  const launchMutation = useLaunchAgent();

  const [values, setValues] = React.useState<Record<string, string>>({});
  const [visibility, setVisibility] = React.useState<ThreadVisibility>(
    ThreadVisibility.ORG,
  );
  // Raw input strings for the optional analysis window in the Advanced
  // section, interpreted per `windowMode` (`datetime-local` values or
  // integer ns since epoch). Both empty (the default) means the launch
  // carries no scope.
  const [windowMode, setWindowMode] =
    React.useState<WindowInputMode>("datetime");
  const [analysisStart, setAnalysisStart] = React.useState("");
  const [analysisEnd, setAnalysisEnd] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const analysisScope = React.useMemo(
    () => buildAnalysisScope(analysisStart, analysisEnd, windowMode),
    [analysisStart, analysisEnd, windowMode],
  );

  const switchWindowMode = (next: WindowInputMode) => {
    if (next === windowMode) {
      return;
    }
    // Carry the entered bounds across the format switch so toggling the
    // mode doesn't discard the user's window.
    setAnalysisStart(convertWindowValue(analysisStart, windowMode, next));
    setAnalysisEnd(convertWindowValue(analysisEnd, windowMode, next));
    setWindowMode(next);
  };

  // Seed empty inputs with either the URL-bound dataset_id (when the
  // user came from a dataset detail page's "Run agent" dialog) or the
  // agent's declared defaults. The rules live in `seedLaunchValues`
  // so they're unit-testable without mounting this component.
  //
  // The effect runs on every agentQuery.data change rather than a
  // single seededRef latch — if TanStack Query serves a stale cache
  // first and refetches the fresh copy moments later, the second
  // arrival's seeds still land.
  React.useEffect(() => {
    const agent = agentQuery.data;
    if (agent === undefined) {
      return;
    }
    setValues((prev) =>
      seedLaunchValues(prev, {
        variables: agent.variables,
        datasetIdFromUrl,
      }),
    );
  }, [agentQuery.data, datasetIdFromUrl]);

  const missingRequired = React.useMemo(() => {
    const agent = agentQuery.data;
    if (agent === undefined) {
      return [];
    }
    return agent.variables.filter(
      (v) => v.required && (values[v.name] ?? "") === "",
    );
  }, [agentQuery.data, values]);

  const canSubmit =
    agentQuery.data !== undefined &&
    orgId !== undefined &&
    agentId !== undefined &&
    missingRequired.length === 0 &&
    analysisScope.error === null &&
    !launchMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || agentId === undefined || orgId === undefined) {
      return;
    }
    setSubmitError(null);
    const request: LaunchAgentRequest = { values, visibility };
    if (analysisScope.scope !== null) {
      request.analysis_scope = analysisScope.scope;
    }
    try {
      const session = await launchMutation.mutateAsync({
        orgId,
        agentId,
        request,
      });
      void navigate(`/agents/threads/${session.thread_id}`);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Launch failed; please retry.",
      );
    }
  };

  const agent = agentQuery.data;
  const headerTitle = agent ? `Launch: ${agent.name}` : "Launch Agent";
  const pageTitle = agent
    ? `Roboto - Launch ${agent.name}`
    : "Roboto - Launch Agent";

  if (agentQuery.isError) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerTitle}>
        <div className={styles.container}>
          <div className={styles.error}>
            Failed to load agent. {agentQuery.error?.message ?? ""}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (agentQuery.isLoading || agent === undefined) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerTitle}>
        <div className={styles.container}>Loading agent&hellip;</div>
      </PageLayout>
    );
  }

  const headerActions = (
    <div className={styles.actions}>
      <Button
        variant="text"
        onClick={() => {
          void navigate(Goto.createAgentRoute(agent.agent_id));
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={() => {
          void handleSubmit();
        }}
      >
        {launchMutation.isPending ? "Launching…" : "Launch"}
      </Button>
    </div>
  );

  return (
    <PageLayout
      title={pageTitle}
      headerContentLeft={headerTitle}
      headerContentRight={headerActions}
    >
      <div className={styles.container}>
        {agent.description && (
          <p className={styles.muted}>{agent.description}</p>
        )}

        <section className={styles.section}>
          {agent.variables.length === 0 ? (
            <p className={styles.muted}>
              This agent has no declared variables. Click <em>Launch</em> to
              launch it as-is.
            </p>
          ) : (
            agent.variables.map((variable) => (
              <VariableInput
                key={variable.name}
                variable={variable}
                value={values[variable.name] ?? ""}
                currentOrg={currentOrganization ?? null}
                onChange={(next) =>
                  setValues((prev) => ({ ...prev, [variable.name]: next }))
                }
              />
            ))
          )}
        </section>

        <section className={styles.section}>
          <div className={styles.field}>
            <span className={styles.label}>Visibility</span>
            <div className={styles.visibilityRow}>
              <label>
                <input
                  type="radio"
                  name="visibility"
                  value={ThreadVisibility.ORG}
                  checked={visibility === ThreadVisibility.ORG}
                  onChange={() => setVisibility(ThreadVisibility.ORG)}
                />{" "}
                Org &mdash; visible on the Threads tab to everyone in your org.
              </label>
              <label>
                <input
                  type="radio"
                  name="visibility"
                  value={ThreadVisibility.PRIVATE}
                  checked={visibility === ThreadVisibility.PRIVATE}
                  onChange={() => setVisibility(ThreadVisibility.PRIVATE)}
                />{" "}
                Private &mdash; only you (and Roboto admins) can read this
                thread.
              </label>
            </div>
          </div>
        </section>

        <Collapsible
          title="Advanced"
          defaultOpen={false}
          subtitle="Optional settings most launches don't need."
        >
          <div className={styles.field}>
            <div className={styles.windowHeader}>
              <span className={styles.label}>Analysis window</span>
              <select
                className={styles.modeSelect}
                value={windowMode}
                onChange={(e) =>
                  switchWindowMode(e.target.value as WindowInputMode)
                }
                aria-label="Analysis window input format"
              >
                <option value="datetime">Date &amp; time</option>
                <option value="iso">ISO 8601</option>
                <option value="ns">ns since epoch</option>
              </select>
            </div>
            <div className={styles.windowRow}>
              <WindowBoundInput
                boundLabel="Start"
                mode={windowMode}
                value={analysisStart}
                onChange={setAnalysisStart}
              />
              <WindowBoundInput
                boundLabel="End"
                mode={windowMode}
                value={analysisEnd}
                onChange={setAnalysisEnd}
              />
            </div>
            <p className={styles.helpText}>
              {WINDOW_MODE_HELP[windowMode]} Leave either bound empty to leave
              it unconstrained.
            </p>
            {analysisScope.error !== null && (
              <p className={styles.fieldError}>{analysisScope.error}</p>
            )}
          </div>
        </Collapsible>

        {submitError && <div className={styles.error}>{submitError}</div>}
      </div>
    </PageLayout>
  );
}

/** Per-mode lead sentence of the analysis-window help text. */
const WINDOW_MODE_HELP: Record<WindowInputMode, string> = {
  datetime:
    "Limit the agent's analysis to data inside this time window (your local timezone).",
  iso: "Limit the agent's analysis to data inside this time window, as ISO 8601 timestamps (UTC unless an offset is given).",
  ns: "Limit the agent's analysis to data inside this time window, in nanoseconds since the Unix epoch.",
};

/** Placeholder for the text-based window-bound inputs (datetime mode
 *  renders a native control and needs none). */
const WINDOW_MODE_PLACEHOLDER: Record<
  Exclude<WindowInputMode, "datetime">,
  string
> = {
  iso: "e.g. 2026-06-11T12:45:30Z",
  ns: "ns since epoch",
};

/**
 * One bound of the Advanced section's analysis window: a `datetime-local`
 * control in datetime mode, a text input for pasting ISO 8601 or ns-epoch
 * timestamps in the other modes. The raw string is interpreted by
 * `buildAnalysisScope`, so this component never parses the value itself.
 */
function WindowBoundInput({
  boundLabel,
  mode,
  value,
  onChange,
}: {
  boundLabel: string;
  mode: WindowInputMode;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className={styles.windowField}>
      <span className={styles.helpText}>{boundLabel}</span>
      {mode === "datetime" ? (
        <input
          type="datetime-local"
          step={1}
          className={`${styles.input} ${styles.windowInput}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          inputMode={mode === "ns" ? "numeric" : undefined}
          placeholder={WINDOW_MODE_PLACEHOLDER[mode]}
          className={`${styles.input} ${styles.windowInput}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

function VariableInput({
  variable,
  value,
  currentOrg,
  onChange,
}: {
  variable: TemplateVariable;
  value: string;
  currentOrg: OrgRecord | null;
  onChange: (next: string) => void;
}) {
  /** The input control for this variable, dispatched on its declared type:
   *  a dataset / collection picker for the typed variants, a plain text
   *  input otherwise. */
  function renderControl() {
    if (variable.type === TemplateVariableType.DATASET_ID) {
      return (
        <DatasetSelector
          datasetId={value === "" ? null : value}
          setDatasetId={(next) => onChange(next ?? "")}
          currentOrg={currentOrg}
        />
      );
    }
    if (variable.type === TemplateVariableType.COLLECTION_ID) {
      return (
        <CollectionSelector
          collectionId={value === "" ? null : value}
          setCollectionId={(next) => onChange(next ?? "")}
          currentOrg={currentOrg}
          contentType={variable.collection_content_type ?? undefined}
        />
      );
    }
    return (
      <input
        id={`variable-${variable.name}`}
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          variable.default !== null && variable.default !== undefined
            ? variable.default
            : undefined
        }
      />
    );
  }

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={`variable-${variable.name}`}>
        {variable.name}
        {variable.required && (
          <span className={styles.required} aria-label="required">
            *
          </span>
        )}
        <span className={styles.typeBadge}>{variable.type}</span>
      </label>
      {renderControl()}
      {variable.description && (
        <p className={styles.helpText}>{variable.description}</p>
      )}
    </div>
  );
}
